import { useState, useMemo } from 'react';
import {
  Typography,
  Box,
  Button,
  IconButton,
  TableBody,
  TableCell,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  Alert,
} from '@mui/material';
import { formatDate, getDurationInDays } from '../utils/formatters';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useScheduleBlocks } from '../hooks/queries';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import { useContextualHint } from '../hooks/useContextualHint';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import PageLayout from '../components/ui/PageLayout';
import PageFilters from '../components/ui/PageFilters';
import ScheduleBlockForm from '../components/forms/ScheduleBlockForm';
import StandardTable from '../components/ui/StandardTable';
import AppSnackbar from '../components/ui/AppSnackbar';

export default function ScheduleBlocksPage() {
  // Obter dica contextual (engine global)
  const hint = useContextualHint('absences', {});
  
  // Paginação e filtros (estados locais)
  const [page, setPage] = useState(0); // MUI usa base 0, API usa base 1
  const [pageSize, setPageSize] = useState(10);
  const [filterProfessional, setFilterProfessional] = useState('');
  const [filterPlace, setFilterPlace] = useState('');

  // Monta filtros para o hook
  const filters = useMemo(() => {
    const f = {};
    if (filterProfessional) f.professionalId = filterProfessional;
    if (filterPlace) f.placeId = filterPlace;
    return f;
  }, [filterProfessional, filterPlace]);

  // React Query hook para bloqueios
  const {
    data,
    scheduleBlocks,
    professionals,
    places,
    totalCount,
    loading,
    error,
    reload,
    createAsync,
    updateAsync,
    deleteAsync,
  } = useScheduleBlocks({ page: page + 1, pageSize, filters });

  // Dialogs e Snackbars
  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  // Maps para lookup rápido
  const professionalsMap = useMemo(() => {
    if (!data?.professionals) return new Map();
    return new Map(data.professionals.map(p => [p.id, p]));
  }, [data?.professionals]);

  const placesMap = useMemo(() => {
    if (!data?.places) return new Map();
    return new Map(data.places.map(p => [p.id, p]));
  }, [data?.places]);

  // Handlers CRUD
  const handleCreate = async (formData) => {
    try {
      // Criar um schedule_block para cada placeProfessionalId
      const promises = formData.placeProfessionalIds.map(ppId =>
        createAsync({
          placeProfessionalId: ppId,
          startDate: formData.startDate,
          endDate: formData.endDate,
          reason: formData.reason,
        })
      );
      
      await Promise.all(promises);
      formDialog.handleClose();
      showSuccess(`${formData.placeProfessionalIds.length} ausência(s) cadastrada(s) com sucesso!`);
    } catch (err) {
      showError('Erro ao cadastrar ausência: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdate = async (formData) => {
    try {
      // Na edição, atualiza apenas o primeiro (deveria ter apenas 1)
      await updateAsync(formDialog.data.id, {
        placeProfessionalId: formData.placeProfessionalIds[0],
        startDate: formData.startDate,
        endDate: formData.endDate,
        reason: formData.reason,
      });
      formDialog.handleClose();
      showSuccess('Ausência atualizada com sucesso!');
    } catch (err) {
      showError('Erro ao atualizar ausência: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = formDialog.isEdit ? handleUpdate : handleCreate;

  // Handlers de paginação
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Limpar filtros
  const handleClearFilters = () => {
    setFilterProfessional('');
    setFilterPlace('');
    setPage(0);
  };

  // Alias para manter compatibilidade com código existente
  const getDuration = getDurationInDays;

  const isActive = (startDate, endDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    return now >= start && now <= end;
  };

  // Loading/Error States
  if (loading && !data) return <LoadingState message="Carregando ausências..." />;
  if (error && !data) return <ErrorState error={error} onRetry={reload} />;

  const tableColumns = [
    { id: 'professional', label: 'Profissional' },
    { id: 'place', label: 'Local' },
    { id: 'startDate', label: 'Início' },
    { id: 'endDate', label: 'Fim' },
    { id: 'duration', label: 'Duração' },
    { id: 'reason', label: 'Motivo' },
    { id: 'status', label: 'Status' },
    { id: 'actions', label: 'Ações', align: 'center' },
  ];

  return (
    <PageLayout
      themeKey="absences"
      subtitle="Gerencie períodos em que profissionais não estarão disponíveis para atendimento"
      actionLabel="Nova Ausência"
      onAction={() => formDialog.handleOpen(null)}
      hint={hint}
    >
      {/* Filtros */}
      <PageFilters
        onClear={handleClearFilters}
        clearDisabled={!filterProfessional && !filterPlace}
      >
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Profissional"
            value={filterProfessional}
            onChange={(e) => {
              setFilterProfessional(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">Todos</MenuItem>
            {professionals.map((prof) => (
              <MenuItem key={prof.id} value={prof.id}>
                {prof.name} {prof.specialty ? `- ${prof.specialty}` : ''}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Local"
            value={filterPlace}
            onChange={(e) => {
              setFilterPlace(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">Todos</MenuItem>
            {places.map((place) => (
              <MenuItem key={place.id} value={place.id}>
                {place.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </PageFilters>

      {/* Empty State */}
      {scheduleBlocks.length === 0 ? (
        <EmptyState
          icon={BlockIcon}
          title="Nenhuma ausência cadastrada"
          message={
            filterProfessional || filterPlace
              ? 'Nenhuma ausência encontrada com os filtros selecionados'
              : 'Clique em "Nova Ausência" para começar'
          }
          action={
            !filterProfessional && !filterPlace ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => formDialog.handleOpen(null)}>
                Nova Ausência
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          {/* Tabela */}
          <StandardTable columns={tableColumns}>
            {scheduleBlocks.map((block) => {
                  const professional = professionalsMap.get(block.professionalId);
                  const place = placesMap.get(block.placeId);
                  const active = isActive(block.startDate, block.endDate);

                  return (
                    <TableRow key={block.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {professional?.name || 'N/A'}
                        </Typography>
                        {professional?.specialty && (
                          <Typography variant="caption" color="text.secondary">
                            {professional.specialty}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{place?.name || 'N/A'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(block.startDate)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(block.endDate)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {getDuration(block.startDate, block.endDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={block.reason}
                        >
                          {block.reason || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={active ? 'Ativo' : 'Inativo'}
                          color={active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => formDialog.handleOpen(block)}
                          title="Editar ausência"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => deleteDialog.handleOpen(block)}
                          title="Excluir ausência"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
            })}
          </StandardTable>

          {/* Paginação */}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </>
      )}

      {/* Dialog de Formulário */}
      <ScheduleBlockForm
        open={formDialog.open}
        onClose={formDialog.handleClose}
        onSubmit={handleSubmit}
        initialData={formDialog.data}
        isEdit={formDialog.isEdit}
        professionals={data?.professionals || []}
        places={data?.places || []}
        placeProfessionals={data?.placeProfessionals || []}
      />

      {/* Dialog de Confirmação de Exclusão */}
      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDeleted={() => {
          deleteDialog.handleClose();
          showSuccess('Excluído com sucesso!');
          reload();
        }}
        deletePath={`/blocks/${deleteDialog.data?.id}`}
        entityName={deleteDialog.data?.reason}
      />

      {/* Snackbar */}
      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </PageLayout>
  );
}
